<template>
  <div class="relative">
    <cms-section
      :class-wrapper="isHidden ? '!hidden' : ''"
      disable-lazy-hydrate
      :first-section="firstElement"
      page-context
      :section="{ name: flagKey, items: allVariants }"
    />
    <cms-section
      :class-wrapper="isHidden ? '' : '!hidden'"
      disable-lazy-hydrate
      page-context
      :section="{ name: flagKey, items: allBaselines }"
    />
    <div class="absolute-0 z-1 bg-white" :class="isHidden ? '' : 'hidden'" :data-test-variant="`skeleton-${flagKey}`">
      <div class="absolute-0 skeleton" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Experiment } from '#types/components/cms/experiment'
import mappings from '#content/mappings'
import { NOTIFICATION_TYPES } from '@optimizely/optimizely-sdk'

const { content } = defineProps<{ content: Experiment, firstElement?: boolean }>()
const { decide, decisions } = useOptimizely()
const { $gtm, $optimizely, $feature } = useNuxtApp()
const route = useRoute()

const mapTargets = (targets, flagKey: string) =>
  targets?.map((target) => {
    const resolved = resolveContentComponent(target, mappings)
    if (!resolved) return null

    return {
      ...resolved,
      dataTestVariant: flagKey
    }
  })?.flat()

const getVariantKey = (key: string): string => (key.match(/\d+-(.+)/) || [])[1] || ''
const getVariantId = (text: string): string => (text.match(/^(\d+)-/) || [])[1] || ''
const getForcedVariantId = (text: string): string => (text.match(/vfcanvas-optimizely_variant_(\d+)/) || [])[1] || ''
const isHidden = ref(true)

const onDecision = ({ type, decisionInfo }) => {
  if (type === 'flag') {
    $gtm.push('optimizely.onOptimizelydecision', decisionInfo)
  }
}

$optimizely.client?.notificationCenter.addNotificationListener(
  NOTIFICATION_TYPES.DECISION,
  onDecision as any
)

const flagKey = content.experienceId?.match(/optimizely_experience_(.+)$/)?.[1] || ''
if ($feature.configP13n) decide(flagKey)

const allVariants = ref(content.variants.map(
  (variant) => mapTargets(variant.targets, `${flagKey}-${getVariantKey(variant.variantId)}`)?.flat()
)?.flat())

const allBaselines = ref(mapTargets(content.baselines, `baseline-${flagKey}`)?.flat())

const forcedVariants = route.query.p13n_testcontext ? JSON.parse(route.query.p13n_testcontext as string)?.variants : []
const forcedVariantsIds = forcedVariants.map((variant) => getForcedVariantId(variant))

const getRevaluatedVariants = () => {
  return forcedVariantsIds?.length
    ? content.variants.find(
      ({ variantId }) => forcedVariantsIds.includes(getVariantId(variantId))
    )?.targets || []
    : content.variants.find(
      ({ variantId }) => getVariantKey(variantId) === decisions.value.get(flagKey)?.variationKey
    )?.targets || []
}

const revaluateDecision = async () => {
  if (!($feature.configP13n && flagKey)) return
  await decide(flagKey)
  allVariants.value = getRevaluatedVariants()
  log.info(`Optimizely variation chosen for component: ${content.id} was ${decisions.value.get(flagKey)?.variationKey}`)
  isHidden.value = allVariants.value.length === 0
}

watch(
  () => route.fullPath,
  () => {
    revaluateDecision()
  }
)

onMounted(() => {
  revaluateDecision()
})

onUnmounted(() => {
  $optimizely.client?.notificationCenter.clearAllNotificationListeners()
})
</script>
